import { useState, type FormEvent } from "react";

import { Button } from "@sirdab/ui";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@sirdab/ui/components/ui/input-otp";

import { useApi, type ApiFailure } from "../../api";
import { ErrorState, Field, StepActions } from "../../components";
import { useFlow } from "../../flow";
import { useMessages, usePortalCopy } from "../../i18n";
import { OfflineNote, useFailureText } from "../shared";
import { StepScaffold } from "../StepScaffold";

export const CODE_LENGTH = 6;

/** `+966551234567` shown as `••••4567`: enough to know which phone, never the number. */
export function maskPhone(phone: string): string {
  return `••••${phone.replace(/\D/g, "").slice(-4)}`;
}

/**
 * The six digits. A wrong, spent and expired code all come back as the same
 * `invalid_code`, and this screen shows the server's words for it and nothing more.
 */
export function CodeStep({
  phone,
  onVerified,
  onChangePhone,
}: {
  /** Canonical phone the code went to. */
  phone: string;
  onVerified: (shopperToken: string) => void;
  onChangePhone: () => void;
}): React.JSX.Element {
  const api = useApi();
  const { t, locale } = useMessages();
  const c = usePortalCopy();
  const { merchantSlug } = useFlow();
  const failureText = useFailureText();

  const [code, setCode] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const codeError =
    touched && code.length < CODE_LENGTH
      ? t("validation.code_required")
      : undefined;

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setTouched(true);
    if (code.length < CODE_LENGTH) return;

    setFailure(null);
    setBusy(true);
    const result = await api.verifyShopperCode({ merchantSlug, phone, code });
    setBusy(false);
    if (!result.ok) {
      setFailure(result);
      // The server's answer is the one thing to read; an empty-field nag on top of it
      // would be a second alert saying less.
      setTouched(false);
      setCode("");
      return;
    }
    onVerified(result.data.shopperToken);
  }

  async function resend(): Promise<void> {
    setFailure(null);
    setResent(false);
    setResending(true);
    const result = await api.requestShopperCode({
      merchantSlug,
      phone,
      locale,
    });
    setResending(false);
    if (!result.ok) {
      setFailure(result);
      return;
    }
    setCode("");
    setResent(true);
  }

  return (
    <StepScaffold
      step="lookup"
      titleKey="screen.lookup.code_title"
      descriptionKey="screen.lookup.code_description"
    >
      <form
        noValidate
        onSubmit={(event) => void submit(event)}
        className="flex flex-col gap-4"
      >
        <OfflineNote />

        <p className="text-body text-subtle">
          {c("code.sent_to")}{" "}
          <span dir="ltr" className="text-foreground tabular-nums">
            {maskPhone(phone)}
          </span>
          {". "}
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={onChangePhone}
          >
            {t("action.change_phone")}
          </Button>
        </p>

        <Field
          label={t("field.code.label")}
          hint={t("field.code.hint")}
          error={codeError}
          required
        >
          {(props) => (
            <InputOTP
              id={props.id}
              aria-describedby={props["aria-describedby"]}
              aria-invalid={props["aria-invalid"]}
              aria-label={t("field.code.label")}
              maxLength={CODE_LENGTH}
              value={code}
              onChange={setCode}
              inputMode="numeric"
              pattern="^[0-9]*$"
              autoComplete="one-time-code"
              autoFocus
              containerClassName="justify-center"
            >
              <InputOTPGroup dir="ltr">
                {Array.from({ length: CODE_LENGTH }, (_, index) => (
                  <InputOTPSlot key={index} index={index} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          )}
        </Field>

        {failure !== null ? (
          <ErrorState message={failureText(failure)} />
        ) : null}

        {resent ? (
          <p role="status" className="text-body text-subtle">
            {c("code.resent")}
          </p>
        ) : null}

        <StepActions
          nextType="submit"
          nextLabel={t("action.verify_code")}
          nextLoading={busy}
        />

        <Button
          type="button"
          variant="link"
          size="xl"
          loading={resending}
          onClick={() => void resend()}
          className="self-center"
        >
          {t("action.resend_code")}
        </Button>
      </form>
    </StepScaffold>
  );
}
